import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDroplet, 
  faHeartPulse, 
  faWeight, 
  faPills, 
  faUtensils, 
  faDumbbell, 
  faBed,
  faChevronDown,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

const formatDateForDisplay = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatTimeForDisplay = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
};

const getActivityIcon = (type) => {
  const iconMap = {
    glucose: faDroplet,
    bloodPressure: faHeartPulse,
    weight: faWeight,
    medication: faPills,
    meal: faUtensils,
    exercise: faDumbbell,
    sleep: faBed
  };
  return iconMap[type] || faDroplet;
};

const getActivityColor = (type) => {
  const colorMap = {
    glucose: 'text-teal-600 bg-teal-50',
    bloodPressure: 'text-red-600 bg-red-50',
    weight: 'text-purple-600 bg-purple-50',
    medication: 'text-blue-600 bg-blue-50',
    meal: 'text-orange-600 bg-orange-50',
    exercise: 'text-green-600 bg-green-50',
    sleep: 'text-indigo-600 bg-indigo-50'
  };
  return colorMap[type] || 'text-gray-600 bg-gray-50';
};

const formatActivityValue = (type, value, unit) => {
  switch (type) {
    case 'glucose':
      const mgdl = (parseFloat(value) * 18).toFixed(0);
      return `${value} mmol/L (${mgdl} mg/dL)`;
    case 'bloodPressure':
      return `${value} mmHg`;
    case 'weight':
      return `${value} kg`;
    case 'sleep':
      return `${value} hours`;
    case 'medication':
    case 'meal':
    case 'exercise':
      return value;
    default:
      return value;
  }
};

const TimelineItem = ({ entry, onEdit, onDelete, isEditing, formData, handleChange, onUpdate, onCancelEdit, isSaving }) => {
  const activityColor = getActivityColor(entry.type);
  const activityIcon = getActivityIcon(entry.type);
  const typeName = entry.type.charAt(0).toUpperCase() + entry.type.slice(1);
  
  return (
    <div className="relative flex items-start space-x-2 sm:space-x-4 pb-2 sm:pb-6">
      {/* Timeline line */}
      <div className="absolute left-4 sm:left-9 top-8 sm:top-10 bottom-0 w-0.5 bg-gray-200"></div>
      
      {/* Activity icon - made smaller */}
      <div className={`flex-shrink-0 w-6 h-6 sm:w-10 sm:h-10 rounded-full ${activityColor} flex items-center justify-center shadow-lg z-10`}>
        <FontAwesomeIcon icon={activityIcon} className="w-2.5 h-2.5 sm:w-4 sm:h-4" />
      </div>
      
      {/* Content - removed container */}
      <div className="flex-1 pr-1 sm:pr-0">
        <div className="flex items-center justify-between mb-0.5 sm:mb-1">
          {/* Time first */}
          <span className="text-xs sm:text-sm font-medium text-gray-600">
            {formatTimeForDisplay(entry.datetime)}
          </span>
          <div className="flex items-center space-x-1 sm:space-x-2 pr-2 sm:pr-6">
            {!isEditing ? (
              <>
                <button
                  onClick={() => onEdit(entry, entry.type)}
                  className="text-teal-600 hover:text-teal-800 p-1"
                >
                  <FontAwesomeIcon icon={faEdit} className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <button
                  onClick={() => onDelete(entry.id, entry.type)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <FontAwesomeIcon icon={faTrash} className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={() => onUpdate(entry.id, entry.type)}
                  disabled={isSaving}
                  className="text-green-600 hover:text-green-800 text-xs sm:text-sm px-2 py-1 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={onCancelEdit}
                  disabled={isSaving}
                  className="text-gray-600 hover:text-gray-800 text-xs sm:text-sm px-2 py-1"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Activity and value on same line */}
        <div className="flex items-center space-x-1 sm:space-x-3 mb-0.5 sm:mb-2">
          <span className="text-xs sm:text-base font-semibold text-gray-900">{typeName}: </span>
          {isEditing ? (
            <input
              type={entry.type === 'sleep' ? 'number' : 'text'}
              step={entry.type === 'sleep' ? '0.5' : undefined}
              value={formData[entry.type] || ''}
              onChange={(e) => handleChange({ target: { name: entry.type, value: e.target.value } })}
              className="flex-1 p-2 border rounded-lg focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            />
          ) : (
            <span className="text-xs sm:text-sm text-gray-700 font-medium">
              {formatActivityValue(entry.type, entry.value, entry.unit)}
            </span>
          )}
        </div>
        
        {entry.notes && (
          <div className="flex items-start space-x-1 sm:space-x-3">
            <span className="text-xs sm:text-sm font-medium text-gray-700">Notes:</span>
            {isEditing ? (
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleChange({ target: { name: 'notes', value: e.target.value } })}
                className="flex-1 p-2 border rounded-lg focus:border-teal-500 focus:ring-1 focus:ring-teal-500 resize-none"
                rows="2"
              />
            ) : (
              <span className="text-gray-600 text-xs sm:text-sm">{entry.notes}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TimelineDay = ({ date, entries, onEdit, onDelete, editingId, formData, handleChange, onUpdate, onCancelEdit, isSaving }) => {
  return (
    <div className="mb-4 sm:mb-8">
      {/* Date header */}
      <div className="flex items-center mb-3 sm:mb-6">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-md">
          {formatDateForDisplay(date)}
        </div>
        <div className="flex-1 h-0.5 bg-gradient-to-r from-teal-200 to-transparent ml-1 sm:ml-4"></div>
      </div>
      
      {/* Timeline items for this day */}
      <div className="ml-1 sm:ml-4">
        {entries.map((entry) => {
          const isEditing = editingId === `${entry.id}-${entry.type}`;
          return (
            <TimelineItem
              key={`${entry.id}-${entry.type}`}
              entry={entry}
              onEdit={onEdit}
              onDelete={onDelete}
              isEditing={isEditing}
              formData={formData}
              handleChange={handleChange}
              onUpdate={onUpdate}
              onCancelEdit={onCancelEdit}
              isSaving={isSaving}
            />
          );
        })}
      </div>
    </div>
  );
};

const TimelineView = ({ 
  entries, 
  onEdit, 
  onDelete, 
  onUpdate, 
  onCancelEdit, 
  editingId, 
  formData, 
  handleChange, 
  isSaving
}) => {
  const [itemsToShow, setItemsToShow] = useState(20);
  const ITEMS_PER_LOAD = 15;

  // Group entries by date (filtering is already done in App.js)
  const groupedEntries = useMemo(() => {
    const sorted = entries.sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
    const limited = sorted.slice(0, itemsToShow);
    
    const grouped = limited.reduce((acc, entry) => {
      const date = new Date(entry.datetime).toDateString();
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(entry);
      return acc;
    }, {});

    return grouped;
  }, [entries, itemsToShow]);

  const hasMoreItems = entries.length > itemsToShow;

  const loadMore = () => {
    setItemsToShow(prev => prev + ITEMS_PER_LOAD);
  };

  if (Object.keys(groupedEntries).length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <FontAwesomeIcon icon={faDroplet} className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No entries found</h3>
        <p className="text-gray-600">
          Start by adding your first health entry!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Timeline container */}
      <div className="relative">
        {Object.entries(groupedEntries).map(([date, dayEntries]) => (
          <TimelineDay
            key={date}
            date={date}
            entries={dayEntries}
            onEdit={onEdit}
            onDelete={onDelete}
            editingId={editingId}
            formData={formData}
            handleChange={handleChange}
            onUpdate={onUpdate}
            onCancelEdit={onCancelEdit}
            isSaving={isSaving}
          />
        ))}
      </div>

      {/* Load More button */}
      {hasMoreItems && (
        <div className="text-center py-6">
          <button
            onClick={loadMore}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-medium rounded-full hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <FontAwesomeIcon icon={faChevronDown} className="w-4 h-4 mr-2" />
            Load More Entries
          </button>
        </div>
      )}
      
      {/* End of timeline indicator */}
      {!hasMoreItems && Object.keys(groupedEntries).length > 0 && (
        <div className="text-center py-6">
          <div className="inline-flex items-center text-gray-500 text-sm">
            <div className="w-8 h-0.5 bg-gray-300 mr-3"></div>
            You've reached the beginning of your health journey
            <div className="w-8 h-0.5 bg-gray-300 ml-3"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineView;
